import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Dictionary, difference, omit, union } from 'lodash';

import { PreferencesApiClient } from '~/services/Http';

export interface UserSettingState {
  data?: object;
  isLoading: boolean;
  failedToLoad: boolean;
  isClearing: boolean;
  failedToClear: boolean;
  isUpdated: boolean;
  hasData: boolean;
}

export interface StoreState {
  userSettings: Dictionary<UserSettingState>;
  /**
   * Stores the list of the user setting keys that
   * were required by some components. When is undefined
   * means that all user settings should be requested.
   */
  requiredUserSettings?: string[];
  /**
   * Stores the list of user setting keys that were
   * requested from the API. This list is updated
   * after the request (is not waiting for data).
   * When is undefined means that UI already has
   * requested all user settings from API.
   */
  requestedUserSettings?: string[];
  isLoadingAllUserSettings: boolean;
  failedToLoadAllUserSettings: boolean;
  isClearingAllUserSettings: boolean;
  failedToClearAllUserSettings: boolean;
}

export const initialUserSettingState: UserSettingState = {
  isLoading: false,
  failedToLoad: false,
  isClearing: false,
  failedToClear: false,
  isUpdated: false,
  hasData: false,
};

export const initialUserSettingsStoreState: StoreState = {
  userSettings: {},
  requiredUserSettings: [],
  requestedUserSettings: [],
  isLoadingAllUserSettings: false,
  failedToLoadAllUserSettings: false,
  isClearingAllUserSettings: false,
  failedToClearAllUserSettings: false,
};

export enum UserSettingsActionKeyPrefixes {
  GET = 'components/UserSettings/GET',
  CLEAR = 'components/UserSettings/CLEAR',
}

export const getUserSettings = createAsyncThunk<
  Dictionary<object>,
  string[] | undefined
>(
  UserSettingsActionKeyPrefixes.GET,
  async (keys: string[] | undefined): Promise<Dictionary<object>> => {
    return await PreferencesApiClient.getPreferences(keys).then((r) => r.data);
  },
);

export const clearUserSettings = createAsyncThunk(
  UserSettingsActionKeyPrefixes.CLEAR,
  async (keys: string[]): Promise<void> => {
    if (!keys.length) {
      return;
    }

    await PreferencesApiClient.removePreferences(keys);
  },
);

export const userSettingsSlice = createSlice({
  name: '~/components/UserSettings',
  initialState: initialUserSettingsStoreState,
  reducers: {
    requireUserSettings: (
      state: StoreState,
      action: PayloadAction<string[] | undefined>,
    ) => {
      state.requiredUserSettings = state.requestedUserSettings
        ? state.requiredUserSettings &&
          action.payload &&
          difference(
            union(state.requiredUserSettings, action.payload),
            state.requestedUserSettings,
          )
        : [];
    },
    updateUserSettings: (
      state: StoreState,
      action: PayloadAction<Dictionary<object>>,
    ) => {
      Object.keys(action.payload).forEach((key) => {
        state.userSettings[key] = {
          ...initialUserSettingState,
          ...state.userSettings[key],
          data: {
            /**
             * Rewriting the data will allow us to much easily
             * update only a part of the data.
             * When needed to clear something from data, just
             * set it undefined.
             */
            ...state.userSettings[key]?.data,
            ...action.payload[key],
          },
          isUpdated: true,
        };
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserSettings.pending, (state, action) => {
        if (!action.meta.arg) {
          state.requiredUserSettings = [];
          state.requestedUserSettings = undefined;
          state.isLoadingAllUserSettings = true;
        } else {
          action.meta.arg.forEach((key) => {
            state.userSettings[key] = {
              ...initialUserSettingState,
              ...state.userSettings[key],
              isLoading: true,
            };
          });

          state.requiredUserSettings =
            state.requiredUserSettings &&
            difference(state.requiredUserSettings, action.meta.arg);

          state.requestedUserSettings =
            state.requestedUserSettings &&
            union(state.requestedUserSettings, action.meta.arg);
        }
      })
      .addCase(getUserSettings.fulfilled, (state, action) => {
        (action.meta.arg || Object.keys(action.payload)).forEach((key) => {
          state.userSettings[key] = {
            ...initialUserSettingState,
            hasData: true,
            data: action.payload[key],
          };
        });

        state.isLoadingAllUserSettings = false;
        state.failedToLoadAllUserSettings = false;
      })
      .addCase(getUserSettings.rejected, (state, action) => {
        if (!action.meta.arg) {
          state.isLoadingAllUserSettings = false;
          state.failedToLoadAllUserSettings = true;
        } else {
          action.meta.arg.forEach((key) => {
            state.userSettings[key] = {
              ...initialUserSettingState,
              ...state.userSettings[key],
              isLoading: false,
              failedToLoad: true,
            };
          });
        }
      })
      .addCase(clearUserSettings.pending, (state, action) => {
        if (!action.meta.arg) {
          state.isClearingAllUserSettings = true;
        } else {
          action.meta.arg.forEach((key) => {
            state.userSettings[key] = {
              ...initialUserSettingState,
              ...state.userSettings[key],
              isClearing: true,
            };
          });
        }
      })
      .addCase(clearUserSettings.fulfilled, (state, action) => {
        if (!action.meta.arg) {
          state.userSettings = {};
          state.isClearingAllUserSettings = false;
          state.failedToClearAllUserSettings = false;
        } else {
          state.userSettings = omit(state.userSettings, action.meta.arg);
        }
      })
      .addCase(clearUserSettings.rejected, (state, action) => {
        if (!action.meta.arg) {
          state.isClearingAllUserSettings = false;
          state.failedToClearAllUserSettings = true;
        } else {
          action.meta.arg.forEach((key) => {
            state.userSettings[key] = {
              ...initialUserSettingState,
              ...state.userSettings[key],
              isClearing: false,
              failedToClear: true,
            };
          });
        }
      });
  },
});

export const { requireUserSettings, updateUserSettings } =
  userSettingsSlice.actions;
export const userSettingsReducer = userSettingsSlice.reducer;
